import { ElMessage } from "element-plus";
import { getAgentId } from "@/config/api";
import { getWidgetAgentData } from "@/utils/widgetAgent";
import { trackWidgetEvent } from "@/utils/widget-analytics";
import { mapFormaPago, mapPaquete } from "@/utils/emissionMappers";
import type { Documento, EmissionCoberturaContext } from "@/types/emission.types";
import type { EmissionSubmitDependencies } from "./contracts";
import { maintainExpandMode, toQuoteContext } from "./helpers";
import { executeUpdateQuoteFlow } from "./update-quote";
import {
  buildCotizacionesEmisionesInsertParams,
  buildPolizasInsertParams,
  emitirAna,
  emitirQualitas,
  getCotizacionesEmisiones,
  getDocumentDownloadUrl,
  getDocumentosXSolicitud,
  insertCotizacionesEmisiones,
  insertPolizas,
  parseIdPolizaFromResponse,
} from "@/services/emissionService";

function persistComplianceEvidence(
  deps: EmissionSubmitDependencies,
  idCotizacion: number,
): void {
  if (typeof window === "undefined") return;

  const userDataRaw = window.localStorage.getItem("user") || "{}";
  const userData = JSON.parse(userDataRaw) as Record<string, unknown>;

  const evidence = {
    idCotizacion,
    accepted: deps.formData.articulo492Aceptado,
    acceptedAt: deps.formData.articulo492FechaAceptacion || new Date().toISOString(),
    emittedAt: new Date().toISOString(),
    user: userData.Usuario || userData.user || userData.Nombre || null,
    documentsCount: deps.formData.articulo492DocumentosCargados,
    documentsMetadata: deps.formData.articulo492DocumentosMetadata,
  };

  window.localStorage.setItem(`compliance-492:${idCotizacion}`, JSON.stringify(evidence));
}

export async function loadDocuments(
  deps: EmissionSubmitDependencies,
  idSolicitud: number,
): Promise<void> {
  deps.state.isLoadingDocumentos.value = true;
  try {
    const docs = await getDocumentosXSolicitud(idSolicitud);
    deps.state.documentosDisponibles.value = docs;
  } finally {
    deps.state.isLoadingDocumentos.value = false;
  }
}

export function downloadDocument(guidDocumento: string, _nombre?: string): void {
  if (!guidDocumento) {
    ElMessage.warning("No se puede descargar: GUID no disponible");
    return;
  }

  window.open(getDocumentDownloadUrl(guidDocumento), "_blank");
  ElMessage.success("Descarga iniciada");
  maintainExpandMode();
}

export async function executeEmitPolicyFlow(
  deps: EmissionSubmitDependencies,
): Promise<void> {
  trackWidgetEvent("begin_checkout", {});
  const props = deps.getProps();
  if (!props.allowEmission) {
    await executeUpdateQuoteFlow(deps);
    return;
  }

  const idCotizacion = props.quotesResponse?.Cobertura?.Id_Cotizacion;
  if (!idCotizacion) {
    ElMessage.error("No se encontró la cotización para emitir");
    return;
  }

  const agentData = getWidgetAgentData();
  const idAgente =
    agentData.Id_Agente ?? (getAgentId() ? Number(getAgentId()) : null);
  if (!idAgente) {
    ElMessage.error("No se encontró información del agente");
    return;
  }

  deps.state.isEmitting.value = true;
  const quote = deps.quoteStore.quoteData;
  const quoteContext = toQuoteContext(quote);
  const cobertura = props.quotesResponse?.Cobertura as
    | EmissionCoberturaContext
    | undefined;

  try {
    let idPoliza: number | null = null;
    let idSolicitud: number | null = null;

    const checkResponse = await getCotizacionesEmisiones(Number(idCotizacion));
    const parsed = parseIdPolizaFromResponse(checkResponse);
    idPoliza = parsed.idPoliza;
    idSolicitud = parsed.idSolicitud;

    if (!idPoliza) {
      const polizaParams = buildPolizasInsertParams(
        quoteContext,
        agentData,
        Number(idCotizacion),
        idAgente,
      );
      const polizaResponse = await insertPolizas(polizaParams);
      const parsedPoliza = parseIdPolizaFromResponse(polizaResponse);
      idPoliza = parsedPoliza.idPoliza;
      idSolicitud = parsedPoliza.idSolicitud;

      if (!idPoliza) {
        ElMessage.error(
          polizaResponse.errorMessage || "No se pudo obtener el ID de la póliza",
        );
        return;
      }
    }

    const formaPago = mapFormaPago(props.selectedPaymentMethod);
    const paquete = mapPaquete(props.selectedPlanType);
    const insertParams = buildCotizacionesEmisionesInsertParams(
      quoteContext,
      deps.formData,
      cobertura,
      agentData,
      {
        idCotizacion: Number(idCotizacion),
        idAgente,
        idPoliza,
        emitida: 1,
        formaPago,
        paquete,
        selectedPlanType: props.selectedPlanType,
        selectedPaymentMethod: props.selectedPaymentMethod,
        vehicleDescription: quote.model?.name ?? "",
      },
    );

    const insertResponse = await insertCotizacionesEmisiones(insertParams);
    if (!insertResponse.success) {
      ElMessage.error(insertResponse.errorMessage || "Error al emitir la póliza");
      return;
    }

    const company = props.selectedCompany ?? "QUALITAS";
    const emitResult =
      company === "ANA" ? await emitirAna(idPoliza) : await emitirQualitas(idPoliza);

    if (emitResult.success && emitResult.data) {
      const responseData = emitResult.data as Record<string, unknown>;
      deps.state.polizaEmitida.value = {
        Id_Poliza: idPoliza,
        Id_Solicitud: idSolicitud ?? 0,
        Poliza_Numero: responseData.successMessage
          ? String(responseData.successMessage)
          : undefined,
      };
      if (idSolicitud) await loadDocuments(deps, idSolicitud);
    }

    deps.state.emissionSuccess.value = emitResult.success;
    if (!emitResult.success) {
      ElMessage.warning(
        `La póliza se creó pero hubo un problema al emitir en ${company === "ANA" ? "ANA" : "Qualitas"}`,
      );
      return;
    }

    trackWidgetEvent("purchase", {
      aseguradora: props.selectedCompany ?? "QUALITAS",
    });
    ElMessage.success("Póliza emitida correctamente");
    persistComplianceEvidence(deps, Number(idCotizacion));
    deps.emitSuccess({
      policyData: {
        policyNumber: deps.state.polizaEmitida.value?.Poliza_Numero,
        idPoliza: deps.state.polizaEmitida.value?.Id_Poliza,
        idSolicitud: deps.state.polizaEmitida.value?.Id_Solicitud,
        planType: props.selectedPlanType,
        primaTotal:
          cobertura?.PrimaTotal != null ? Number(cobertura.PrimaTotal) : undefined,
      },
      documentos: deps.state.documentosDisponibles.value as Documento[],
    });

    await deps.sendEmailInBackground({
      idCotizacion: Number(idCotizacion),
      idPoliza: deps.state.polizaEmitida.value?.Id_Poliza ?? undefined,
    });
  } catch {
    ElMessage.error("Error al emitir la póliza");
  } finally {
    deps.state.isEmitting.value = false;
    maintainExpandMode();
  }
}
